import React, { useEffect, useRef, useState } from 'react';
import logo from '../assets/LED-Speed-Signs-Logo.png';

const InstagramIcon = (props) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookIcon = (props) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

// Link with the navbar's "lights-up" underline that grows from the left on hover.
const FooterLink = ({ href, children, ...props }) => (
  <a
    href={href}
    {...props}
    className="group relative inline-flex w-fit items-center gap-2 text-sm text-slate-600 transition-colors hover:text-[#0f172a]"
  >
    {children}
    <span className="absolute -bottom-0.5 left-0 h-0.5 w-0 rounded-full bg-[linear-gradient(90deg,#ff3b30,#30d158,#0a84ff)] transition-all duration-300 group-hover:w-full" />
  </a>
);

const Footer = () => {
  const footerRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  // Reveal the bottom LED line once the footer scrolls into view; the line then
  // spreads out from the centre to both sides (the scale-x transition below).
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <footer ref={footerRef} className="relative mt-auto border-t border-slate-200 bg-white">
      <div className="mx-auto max-w-[1200px] px-6 py-14">
        <div className="flex flex-col gap-10 md:flex-row md:items-start md:justify-between">
          {/* Brand */}
          <div className="flex max-w-[280px] flex-col gap-4">
            <img src={logo} alt="LED Speed Signs" className="h-16 w-32 -mt-8" />
            <p className="text-sm leading-relaxed text-slate-500">
              Australian-made solar LED speed signs, engineered for safer roads and reliable, remote
              operation.
            </p>
            <p className="text-sm text-slate-400">
              © {new Date().getFullYear()} Sign Blast Pty Ltd. All rights reserved.
            </p>
          </div>

          {/* Contact */}
          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Contact</h3>
            <FooterLink href="mailto:info@ledspeedsigns.com.au">info@ledspeedsigns.com.au</FooterLink>
            <FooterLink href="tel:+0753536011">07 5353 6011</FooterLink>
            <p className="text-sm leading-relaxed text-slate-500">
              2/16 Aerodrome Road
              <br />
              Caboolture QLD 4510
            </p>
          </div>

          {/* Social */}
          <div className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Follow</h3>
            <FooterLink href="https://www.instagram.com/signblast.au" target="_blank" rel="noopener noreferrer">
              <InstagramIcon />
              Instagram
            </FooterLink>
            <FooterLink href="https://www.facebook.com/SignBlast" target="_blank" rel="noopener noreferrer">
              <FacebookIcon />
              Facebook
            </FooterLink>
          </div>
        </div>
      </div>

      {/* Animated LED accent line + glow along the BOTTOM edge. On reveal it spreads
          from the centre out to both sides via the scale-x transition. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-px origin-center transition-transform duration-[1100ms] ease-out ${
          revealed ? 'scale-x-100' : 'scale-x-0'
        }`}
      >
        <div className="absolute inset-x-0 bottom-0 h-8 bg-[linear-gradient(to_top,rgba(255,255,255,0.55),rgba(180,186,198,0.3),transparent)]" />
        <div className="absolute inset-x-0 bottom-0 h-px animate-led-sweep rounded-full [background-size:200%_100%] bg-[linear-gradient(90deg,transparent,rgba(255,59,48,0.2),#ff3b30,#30d158,#0a84ff,rgba(10,132,255,0.2),transparent)] shadow-[0_0_14px_rgba(190,196,206,0.55)] motion-reduce:animate-none" />
      </div>
    </footer>
  );
};

export default Footer;
